// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullRegattaResultOverall,
  $FullRegattaResultOverallEntry,
  $Model,
} from 'common.data';

// Global Components
import * as React from 'react';

// Helpers
import _ from 'lodash';
import {
  durationFormat,
  getRegattaEntrySubTitle,
  getRegattaEntryTitle,
  getTimeDiff,
  sortResults,
} from 'common.helpers/dist/browser';

// Types
type $View = 'races' | 'type';

type $OptionalProps = {
  onShowLess?: () => unknown;
};

type $Props = $OptionalProps & $Translate & {
  item: $FullRegattaResultOverall;
  regatta: $Model<'Regatta'>;
};

type $State = {
  showAll: boolean;
  view: $View;
};

class RegattaResultOverallTableView extends React.Component<$Props, $State> {
  static defaultProps: $OptionalProps = {
    onShowLess: undefined,
  };

  constructor(props: $Props) {
    super(props);

    this.state = {
      showAll: false,
      view: 'type',
    };
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  renderShowToggleButton() {
    const {
      item,
      onShowLess,
      t,
    } = this.props;
    const {
      showAll,
    } = this.state;

    const show: boolean = item.RegattaResultOverallEntries.length > 10;

    return (show === true ? (
      <div className={'text-center px-5 py-3'}>
        <button
          className={'btn btn_secondary btn_outlined uppercase py-1'}
          onClick={async () => {
            if (showAll === true && onShowLess) {
              onShowLess();
            }

            await this.setStateAsync({
              showAll: !showAll,
            });
          }}
          type={'button'}
        >
          {showAll === true ? t('Show less') : t('Show full list')}
        </button>
      </div>
    ) : null);
  }

  renderViewButton() {
    const {
      item,
      t,
    } = this.props;
    const {
      view,
    } = this.state;

    return (view === 'type' ? (
      <button
        className={'btn btn_light btn_outlined font-normal py-0 px-2 uppercase'}
        onClick={() => this.setState({
          view: 'races',
        })}
        type={'button'}
      >
        {item.Type}
      </button>
    ) : (
      <button
        className={'btn btn_light btn_outlined font-normal py-0 px-2 uppercase'}
        onClick={() => this.setState({
          view: 'type',
        })}
        type={'button'}
      >
        {t('Races')}
      </button>
    ));
  }

  renderHead() {
    const {
      item,
    } = this.props;

    return (
      <thead>
        <tr>
          <th
            className={'text-left text-green-600 uppercase'}
            colSpan={3}
          >
            {item.Name}
          </th>
        </tr>
      </thead>
    );
  }

  renderBody() {
    const {
      item,
      regatta,
      t,
    } = this.props;
    const {
      showAll,
      view,
    } = this.state;

    const sortedRegattaResultOverallEntries: Array<$FullRegattaResultOverallEntry> = sortResults(
      item.RegattaResultOverallEntries,
    );

    return (
      <tbody>
        <tr className={'text-gray-600 uppercase'}>
          <td className={'text-left text-xs py-1-i pl-2'}>
            {t('Entries')}
          </td>
          <td className={'text-center text-xs py-1-i w-20'}>
            {this.renderViewButton()}
          </td>
          <td className={'text-center text-xs py-1-i w-24'}>
            {t('Plc.')}
          </td>
        </tr>
        {_.take(
          sortedRegattaResultOverallEntries,
          showAll ? 100 : 10,
        ).map((
          entry: $FullRegattaResultOverallEntry,
        ) => (
          <tr key={entry.id}>
            <td className={'leading-none py-1-i'}>
              {entry.RegattaResultTableEntry && (
                <>
                  <div className={'text-base'}>
                    {getRegattaEntryTitle({
                      bowNoEnabled: regatta.BowNoEnabled,
                      countryEnabled: regatta.CountryEnabled,
                      regattaEntry: entry.RegattaResultTableEntry.RegattaEntry,
                    })}
                  </div>
                  <small className={'leading-3 text-gray-600'}>
                    {getRegattaEntrySubTitle({
                      countryEnabled: regatta.CountryEnabled,
                      regattaEntry: entry.RegattaResultTableEntry.RegattaEntry,
                    })}
                  </small>
                </>
              )}
            </td>
            <td className={'text-center w-20 whitespace-nowrap py-1-i'}>
              {view === 'type' ? (
                <div className={'leading-none'}>
                  <div>
                    {durationFormat(entry.Points)}
                  </div>
                  <small>
                    {getTimeDiff(
                      entry.Points,
                      _.map(
                        item.RegattaResultOverallEntries,
                        'Points',
                      ),
                    )}
                  </small>
                </div>
              ) : (
                <div>
                  {entry.Races}
                </div>
              )}
            </td>
            <td className={'text-center text-lg font-bold bg-blue text-white w-24 whitespace-nowrap'}>
              {entry.Place}
            </td>
          </tr>
        ))}
      </tbody>
    );
  }

  render() {
    const {
      item,
    } = this.props;
    const {
      showAll,
    } = this.state;

    const shouldFade: boolean = showAll === false && item.RegattaResultOverallEntries.length > 10;

    return (
      <div
        className={'RegattaResultOverallTable overflow-y-auto'}
        id={`RegattaResultOverallTable-${item.id}`}
      >
        <table className={`table table_hoverable w-full ${shouldFade === true ? 'fade-content max-h-80' : ''}`}>
          {this.renderHead()}
          {this.renderBody()}
        </table>
        {this.renderShowToggleButton()}
      </div>
    );
  }
}

export default RegattaResultOverallTableView;
